import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, MenuItem } from "@vapor/react-material";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { DocLabelUpdateAction } from "../../../actions/labels";
import { getOrderDocument } from "../../../actions/orders";
import {
    hideLockOverlay,
    showLockOverlay,
    showNotificationOverflow,
} from "../../../actions/ui";
import { OrderLabel } from "../../../interfaces/label";
import { OrderInterface } from "../../../interfaces/orders";
import { ProductInterface } from "../../../interfaces/products";
import { downloadContent } from "../../../services/files-operations";
import { textEllipsis } from "../../../services/text-operations";
import Tag from "../../vapor/tag";
import { TagColor } from "../../vapor/tag/colors";
import Tooltip from "../../vapor/tooltip";
import ManageDocLabelDialog from "../labels/manageDocLabelDialog";
import "./style.scss";

declare const window: any;

interface ProductCardProps {
    item: OrderInterface;
    product?: ProductInterface;
    orderLabels?: Array<string>;
    labelColorsMapping: Record<string, TagColor>;
    availableLabels?: Array<OrderLabel>;
    onSelect: () => void;
    onLabelsUpdate: (docLabelActions: Array<DocLabelUpdateAction>) => void;
}

const OrderCard = (props: ProductCardProps) => {
    const products = useSelector((state: RootState) => state.products.data);
    const dispatch: any = useDispatch();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const orderLabels =
        props.orderLabels?.map((labelString) => {
            return {
                name: labelString,
                color:
                    props.labelColorsMapping[labelString.toLowerCase()] ||
                    TagColor.RustyRed,
            };
        }) || [];

    // new: compute visible labels and remaining
    const maxLabels = 4; // TODO: make this responsive
    const visibleLabels = orderLabels.slice(0, maxLabels);
    const remainingCount = Math.max(
        0,
        orderLabels.length - visibleLabels.length,
    );
    const remainingNames = orderLabels
        .slice(maxLabels)
        .map((l) => l.name)
        .join(", ");

    const downloadDocument = (format?: string) => {
        dispatch(showLockOverlay(true, "Download documento in corso..."));
        dispatch(getOrderDocument(props.item.order_id, format || "PDF"))
            .then((res: any) => {
                dispatch(hideLockOverlay());
                downloadContent(
                    res,
                    `${props.item?.product}_${props.item.fiscal_code}.${format || "PDF"}`,
                );
                window?.aptrinsic?.(
                    "track",
                    "business-information:DOWNLOAD-DOCUMENT",
                );
            })
            .catch((err: any) => {
                dispatch(hideLockOverlay());
                dispatch(
                    showNotificationOverflow(
                        "Errore",
                        "Si è verificato un errore durante il download del documento",
                        "error",
                    ),
                );
            });
    };

    const onSave = (docLabelActions: Array<DocLabelUpdateAction>) => {
        props.onLabelsUpdate(docLabelActions);
    };

    let formatsText = "";

    products
        .find((product) => product.name === props.product?.name)
        ?.formats.map((format) => {
            return (formatsText += `${formatsText.length > 0 ? ", " : ""}${format}`);
        });

    return (
        <div className="bis-cards-order">
            <div className="bis-cards-order-icon">
                <FontAwesomeIcon
                    icon={[
                        "far",
                        props.product?.product &&
                        ["BILANCIO_XBRL", "BILANCIO_COMPLETO"].indexOf(
                            props.product?.product,
                        ) > -1
                            ? "code"
                            : "file-pdf",
                    ]}
                />
            </div>
            <div className="bis-cards-order-info">
                <div className="bis-cards-order-info-company">
                    {textEllipsis(props.item.name || "", 50)}{" "}
                    <span>
                        {props.item.piva && "P.IVA"} {props.item.piva}
                    </span>
                </div>
                <div className="bis-cards-order-info-title">
                    {props.product && props.product.name}{" "}
                    {props.product && (
                        <div className="bis-cards-order-info-title-tooltip">
                            <Tooltip
                                width={220}
                                content={
                                    <div className="bis-cards-order-info-title-tooltip-root">
                                        <div>{props.product.name}</div>
                                        <div>{props.product.description}</div>
                                    </div>
                                }
                            >
                                <FontAwesomeIcon
                                    icon={["far", "info-circle"]}
                                />
                            </Tooltip>
                        </div>
                    )}
                </div>
            </div>
            <div className="bis-cards-order-labelbox">
                <ManageDocLabelDialog
                    onSave={onSave}
                    presetLabels={props.availableLabels}
                    docLabels={orderLabels}
                />
                <div>
                    {visibleLabels.map((label: OrderLabel) => {
                        return (
                            <Tag
                                color={label.color}
                                key={"order-label-" + label.name}
                            >
                                {label.name}
                            </Tag>
                        );
                    })}
                    {remainingCount > 0 && (
                        <Tooltip
                            width={220}
                            content={
                                <div className="bis-cards-order-info-title-tooltip-root">
                                    <div>
                                        <FormattedMessage id="general.otherLabels" />
                                    </div>
                                    <div>{remainingNames}</div>
                                </div>
                            }
                        >
                            <Tag
                                color={TagColor.blackPearl}
                                ghost
                                key={"order-label-more"}
                            >
                                {`+${remainingCount}`}
                            </Tag>
                        </Tooltip>
                    )}
                </div>
            </div>
            <div className="bis-cards-order-status">
                {props.item.status !== "COMPLETE" && (
                    <Tag color={TagColor.blueSapphire} ghost>
                        {
                            <FormattedMessage
                                id={`orders.statuses.${props.item.status}`}
                            />
                        }
                    </Tag>
                )}
            </div>
            <div className="bis-cards-order-date">
                Acquistato il{" "}
                <span>
                    {useIntl().formatDate(props.item.request_date, {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                    })}
                </span>
            </div>
            <div className="bis-cards-order-format">
                Formato <div>{formatsText}</div>
            </div>
            <div
                className={`bis-cards-order-actions${props.item.status !== "COMPLETE" ? " disabled" : ""}`}
            >
                {props.product?.formats.length === 1 && (
                    <div
                        onClick={() => {
                            if (props.item.status === "COMPLETE") {
                                downloadDocument(props.product?.formats[0]);
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={["far", "download"]} />
                    </div>
                )}
                {props.product?.formats.length !== 1 && (
                    <>
                        <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                                "aria-labelledby": "basic-button",
                            }}
                        >
                            {props.product?.formats.map((it) => {
                                return (
                                    <MenuItem
                                        onClick={() => {
                                            downloadDocument(it);
                                            handleClose();
                                        }}
                                        key={"order-download_" + it}
                                    >
                                        <FormattedMessage id="actions.download" />
                                        <span style={{ marginLeft: 4 }}>
                                            {it.toLocaleLowerCase()}
                                        </span>
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                        <div
                            onClick={(e) => {
                                if (props.item.status === "COMPLETE") {
                                    handleClick(e);
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={["far", "download"]} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
export default OrderCard;
